import { useCallback, useMemo, useState, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Node,
  Edge,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  ReactFlowProvider,
  Panel,
  useReactFlow,
  OnConnectEnd,
} from 'reactflow';
import 'reactflow/dist/style.css';

import { StepNode } from './StepNode';
import { ProjectHeader } from './ProjectHeader';
import { NodeData, STEP_X_SPACING, NODE_Y_SPACING, START_X, START_Y } from '@/types/node';
import { useProjectStore } from '@/store/projectStore';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Trash2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';

const nodeTypes = {
  stepNode: StepNode,
};

// 노드 자동 배치 함수
const calculateNodePosition = (stepType: number, branchIndex: number) => {
  return {
    x: START_X + stepType * STEP_X_SPACING,
    y: START_Y + branchIndex * NODE_Y_SPACING,
  };
};

// 삭제된 노드 타입
interface DeletedNode {
  node: Node<NodeData>;
  edges: Edge[];
  deletedAt: Date;
}

function MindMapCanvasInner() {
  const { currentProject } = useProjectStore();
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [deletedNodes, setDeletedNodes] = useState<DeletedNode[]>([]);
  const [editingNode, setEditingNode] = useState<Node<NodeData> | null>(null);
  const [editForm, setEditForm] = useState({ label: '', description: '' });
  const connectingNodeId = useRef<string | null>(null);
  const { screenToFlowPosition } = useReactFlow();

  // 초기 노드 생성 (프로젝트 로드 시)
  const initializeNodes = useCallback((projectId: string) => {
    // Step 0 노드 자동 생성
    const step0Node: Node<NodeData> = {
      id: `${projectId}-step0-0`,
      type: 'stepNode',
      position: calculateNodePosition(0, 0),
      data: {
        id: `${projectId}-step0-0`,
        stepType: 0,
        stepName: '장르/소재 선택',
        label: '프로젝트 시작',
        data: null,
        isExpanded: true,
        isCompleted: false,
        requiredFields: ['메인 장르', '시대/배경', '톤/분위기'],
        completedFields: [],
      },
    };

    setNodes([step0Node]);
    setEdges([]);
    setSelectedNodeId(step0Node.id);
  }, [setNodes, setEdges]);

  // 새 분기 노드 추가
  const addBranchNode = useCallback(
    (parentNodeId: string, stepType: number, position?: { x: number; y: number }) => {
      const parentNode = nodes.find((n) => n.id === parentNodeId);
      if (!parentNode) return;

      // 같은 단계의 기존 노드 개수 계산
      const sameStepNodes = nodes.filter((n) => n.data.stepType === stepType);
      const branchIndex = sameStepNodes.length;

      const newNode: Node<NodeData> = {
        id: `${currentProject?.projectId}-step${stepType}-${branchIndex}-${Date.now()}`,
        type: 'stepNode',
        position: position || calculateNodePosition(stepType, branchIndex),
        data: {
          id: `${currentProject?.projectId}-step${stepType}-${branchIndex}-${Date.now()}`,
          stepType: stepType as any,
          stepName: `Step ${stepType}`,
          label: `분기 ${branchIndex + 1}`,
          data: null,
          isExpanded: false,
          isCompleted: false,
          requiredFields: [],
          completedFields: [],
        },
      };

      const newEdge: Edge = {
        id: `e-${parentNodeId}-${newNode.id}`,
        source: parentNodeId,
        target: newNode.id,
        type: 'smoothstep',
        animated: true,
      };

      setNodes((nds) => [...nds, newNode]);
      setEdges((eds) => [...eds, newEdge]);
      setSelectedNodeId(newNode.id);

      return newNode.id;
    },
    [nodes, currentProject, setNodes, setEdges]
  );

  // 노드 클릭 핸들러
  const onNodeClick = useCallback(
    (event: React.MouseEvent, node: Node<NodeData>) => {
      setSelectedNodeId(node.id);
    },
    []
  );

  // 노드 더블클릭으로 편집
  const onNodeDoubleClick = useCallback(
    (event: React.MouseEvent, node: Node<NodeData>) => {
      setEditingNode(node);
      setEditForm({
        label: node.data.label || '',
        description: node.data.description || '',
      });
    },
    []
  );

  // 연결 생성 핸들러
  const onConnect = useCallback(
    (params: Connection) => {
      setEdges((eds) =>
        addEdge(
          {
            ...params,
            type: 'smoothstep',
            animated: true,
          },
          eds
        )
      );
    },
    [setEdges]
  );

  // 연결 시작 시
  const onConnectStart = useCallback(
    (_: any, { nodeId }: { nodeId: string | null }) => {
      connectingNodeId.current = nodeId;
    },
    []
  );

  // 연결 종료 시 (빈 공간에 드롭하면 새 노드 생성)
  const onConnectEnd = useCallback<OnConnectEnd>(
    (event) => {
      if (!connectingNodeId.current) return;

      const targetIsPane = (event.target as Element)?.classList.contains('react-flow__pane');

      if (targetIsPane && event instanceof MouseEvent) {
        const sourceNode = nodes.find((n) => n.id === connectingNodeId.current);
        if (!sourceNode) return;

        const position = screenToFlowPosition({
          x: event.clientX,
          y: event.clientY,
        });

        const nextStepType = Math.min(sourceNode.data.stepType + 1, 10);
        addBranchNode(sourceNode.id, nextStepType, position);
      }
    },
    [nodes, screenToFlowPosition, addBranchNode]
  );

  // 노드 삭제 핸들러
  const deleteNode = useCallback(
    (nodeId: string) => {
      const nodeToDelete = nodes.find((n) => n.id === nodeId);
      if (!nodeToDelete) return;

      // Step 0 노드는 삭제 불가
      if (nodeToDelete.data.stepType === 0) {
        alert('시작 노드는 삭제할 수 없습니다.');
        return;
      }

      // 연결된 엣지 찾기
      const relatedEdges = edges.filter(
        (e) => e.source === nodeId || e.target === nodeId
      );

      // 휴지통에 추가
      setDeletedNodes((prev) => [
        ...prev,
        {
          node: nodeToDelete,
          edges: relatedEdges,
          deletedAt: new Date(),
        },
      ]);

      // 노드와 엣지 삭제
      setNodes((nds) => nds.filter((n) => n.id !== nodeId));
      setEdges((eds) => eds.filter(
        (e) => e.source !== nodeId && e.target !== nodeId
      ));

      // 선택 해제
      if (selectedNodeId === nodeId) {
        setSelectedNodeId(null);
      }
    },
    [nodes, edges, selectedNodeId, setNodes, setEdges]
  );

  // Delete 키로 노드 삭제
  const onNodesDelete = useCallback(
    (nodesToDelete: Node[]) => {
      nodesToDelete.forEach((node) => {
        deleteNode(node.id);
      });
    },
    [deleteNode]
  );

  // 노드 복구
  const restoreNode = useCallback(
    (deletedNode: DeletedNode) => {
      setNodes((nds) => [...nds, deletedNode.node]);
      setEdges((eds) => [...eds, ...deletedNode.edges]);
      setDeletedNodes((prev) => prev.filter((dn) => dn.node.id !== deletedNode.node.id));
    },
    [setNodes, setEdges]
  );

  // 노드 편집 저장
  const saveNodeEdit = useCallback(() => {
    if (!editingNode) return;

    setNodes((nds) =>
      nds.map((node) => {
        if (node.id === editingNode.id) {
          return {
            ...node,
            data: {
              ...node.data,
              label: editForm.label,
              description: editForm.description,
            },
          };
        }
        return node;
      })
    );

    setEditingNode(null);
  }, [editingNode, editForm, setNodes]);

  // 선택된 노드 가져오기
  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId),
    [nodes, selectedNodeId]
  );

  // 프로젝트 변경 시 노드 초기화
  useMemo(() => {
    if (currentProject) {
      initializeNodes(currentProject.projectId);
    }
  }, [currentProject, initializeNodes]);

  return (
    <div className="w-full h-screen relative">
      {/* 상단 헤더 */}
      <ProjectHeader />

      {/* React Flow 캔버스 */}
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onConnectStart={onConnectStart}
        onConnectEnd={onConnectEnd}
        onNodeClick={onNodeClick}
        onNodeDoubleClick={onNodeDoubleClick}
        onNodesDelete={onNodesDelete}
        nodeTypes={nodeTypes}
        fitView
        minZoom={0.2}
        maxZoom={1.5}
        defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
        className="bg-gray-50"
        deleteKeyCode="Delete"
      >
        <Background gap={16} size={1} color="#e5e7eb" />
        <Controls />
        <MiniMap
          nodeColor={(node) => {
            const data = node.data as NodeData;
            return data.isCompleted ? '#10b981' : '#94a3b8';
          }}
          className="!bg-white !border-2 !border-gray-300"
        />

        {/* 하단 컨트롤 패널 */}
        <Panel position="bottom-center" className="space-x-2">
          <Button
            onClick={() => {
              if (selectedNode && selectedNode.data.stepType < 10) {
                addBranchNode(selectedNode.id, selectedNode.data.stepType + 1);
              }
            }}
            disabled={!selectedNode || selectedNode.data.stepType >= 10}
            className="shadow-md"
          >
            + 다음 단계 추가
          </Button>
          {selectedNode && selectedNode.data.stepType > 0 && (
            <Button
              onClick={() => deleteNode(selectedNode.id)}
              variant="destructive"
              className="shadow-md"
            >
              선택 노드 삭제
            </Button>
          )}
        </Panel>

        {/* 좌하단 휴지통 */}
        <Panel position="bottom-left">
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                className="relative shadow-md"
              >
                <Trash2 className="w-4 h-4" />
                {deletedNodes.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                    {deletedNodes.length}
                  </span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="start">
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">휴지통</h4>
                {deletedNodes.length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    삭제된 노드가 없습니다.
                  </p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {deletedNodes.map((deletedNode, index) => (
                      <div
                        key={deletedNode.node.id}
                        className="flex items-center justify-between p-2 border rounded-md"
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {deletedNode.node.data.label}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Step {deletedNode.node.data.stepType}
                          </p>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => restoreNode(deletedNode)}
                        >
                          복구
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PopoverContent>
          </Popover>
        </Panel>
      </ReactFlow>

      {/* 노드 편집 다이얼로그 */}
      <Dialog open={!!editingNode} onOpenChange={(open) => !open && setEditingNode(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>노드 편집</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="label">라벨</Label>
              <Input
                id="label"
                value={editForm.label}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, label: e.target.value }))
                }
                placeholder="노드 라벨을 입력하세요"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">설명</Label>
              <Textarea
                id="description"
                value={editForm.description}
                onChange={(e) =>
                  setEditForm((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder="노드 설명을 입력하세요"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingNode(null)}>
              취소
            </Button>
            <Button onClick={saveNodeEdit}>저장</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function MindMapCanvas() {
  return (
    <ReactFlowProvider>
      <MindMapCanvasInner />
    </ReactFlowProvider>
  );
}
